import React, { useState } from 'react';
import { Card } from '../Shared/Card';
import { AlertTriangleIcon, CheckCircleIcon, ClockIcon, FileTextIcon, FilterIcon, ArrowRightIcon, XIcon, CheckIcon, InfoIcon } from 'lucide-react';
type ExceptionStatus = 'open' | 'in_review' | 'resolved' | 'waived';
type ExceptionSeverity = 'high' | 'medium' | 'low';
type ExceptionCategory = 'documentation' | 'underwriting' | 'compliance' | 'valuation';
interface Exception {
  id: string;
  title: string;
  description: string;
  loanId: string;
  borrower: string;
  dateIdentified: string;
  status: ExceptionStatus;
  severity: ExceptionSeverity;
  category: ExceptionCategory;
  assignee: string;
  dueDate: string;
  notes?: string;
}
export function Exceptions() {
  const [activeFilter, setActiveFilter] = useState<ExceptionStatus | 'all'>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<ExceptionSeverity | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<ExceptionCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Sample exceptions data
  const exceptions: Exception[] = [{
    id: 'EXC-001',
    title: 'Missing Borrower ID Verification',
    description: 'Borrower ID verification document is missing from the loan file.',
    loanId: '#005312',
    borrower: 'Alex Young',
    dateIdentified: '2023-05-01',
    status: 'open',
    severity: 'high',
    category: 'documentation',
    assignee: 'Sarah Wilson',
    dueDate: '2023-05-15'
  }, {
    id: 'EXC-002',
    title: 'LTV Exceeds Guideline',
    description: 'Loan-to-value ratio exceeds maximum guideline of 75%.',
    loanId: '#005331',
    borrower: 'James Wilson',
    dateIdentified: '2023-05-02',
    status: 'in_review',
    severity: 'medium',
    category: 'underwriting',
    assignee: 'Michael Brown',
    dueDate: '2023-05-16',
    notes: 'Exception is being reviewed by senior underwriter for approval.'
  }, {
    id: 'EXC-003',
    title: 'Appraisal Discrepancy',
    description: 'Significant discrepancy between two appraisal values.',
    loanId: '#005342',
    borrower: 'Sarah Miller',
    dateIdentified: '2023-04-28',
    status: 'resolved',
    severity: 'medium',
    category: 'valuation',
    assignee: 'Robert Johnson',
    dueDate: '2023-05-12',
    notes: 'Third appraisal was ordered and confirmed the lower value.'
  }, {
    id: 'EXC-004',
    title: 'Incomplete Insurance Documentation',
    description: 'Property insurance documentation is incomplete.',
    loanId: '#005355',
    borrower: 'Robert Johnson',
    dateIdentified: '2023-05-03',
    status: 'open',
    severity: 'low',
    category: 'documentation',
    assignee: 'Sarah Wilson',
    dueDate: '2023-05-17'
  }, {
    id: 'EXC-005',
    title: 'DSCR Below Minimum',
    description: 'Debt service coverage ratio is below the minimum requirement of 1.25.',
    loanId: '#005324',
    borrower: 'Maria Chen',
    dateIdentified: '2023-04-30',
    status: 'waived',
    severity: 'high',
    category: 'underwriting',
    assignee: 'Michael Brown',
    dueDate: '2023-05-14',
    notes: 'Waived by Chief Credit Officer due to strong borrower financials and low LTV.'
  }];
  // Filter exceptions based on active filters
  const filteredExceptions = exceptions.filter(exception => {
    // Filter by status
    if (activeFilter !== 'all' && exception.status !== activeFilter) {
      return false;
    }
    // Filter by severity
    if (selectedSeverity !== 'all' && exception.severity !== selectedSeverity) {
      return false;
    }
    // Filter by category
    if (selectedCategory !== 'all' && exception.category !== selectedCategory) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !exception.title.toLowerCase().includes(searchQuery.toLowerCase()) && !exception.loanId.toLowerCase().includes(searchQuery.toLowerCase()) && !exception.borrower.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Get counts for status tabs
  const getStatusCount = (status: ExceptionStatus | 'all') => {
    if (status === 'all') {
      return exceptions.length;
    }
    return exceptions.filter(e => e.status === status).length;
  };
  // Get status badge color
  const getStatusBadgeClass = (status: ExceptionStatus) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800';
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'waived':
        return 'bg-blue-100 text-blue-800';
    }
  };
  // Get severity badge color
  const getSeverityBadgeClass = (severity: ExceptionSeverity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
    }
  };
  // Get category badge color
  const getCategoryBadgeClass = (category: ExceptionCategory) => {
    switch (category) {
      case 'documentation':
        return 'bg-purple-100 text-purple-800';
      case 'underwriting':
        return 'bg-blue-100 text-blue-800';
      case 'compliance':
        return 'bg-orange-100 text-orange-800';
      case 'valuation':
        return 'bg-indigo-100 text-indigo-800';
    }
  };
  // Format status for display
  const formatStatus = (status: ExceptionStatus) => {
    switch (status) {
      case 'open':
        return 'Open';
      case 'in_review':
        return 'In Review';
      case 'resolved':
        return 'Resolved';
      case 'waived':
        return 'Waived';
    }
  };
  return <div className="space-y-6" data-id="element-592">
      <div className="flex justify-between items-center" data-id="element-593">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-594">
          Exceptions Management
        </h2>
        <div className="flex space-x-3" data-id="element-595">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" data-id="element-596">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-597" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" data-id="element-598">
            <AlertTriangleIcon className="w-4 h-4 mr-2" data-id="element-599" />
            Create Exception
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-start" data-id="element-600">
        <InfoIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-601" />
        <div data-id="element-602">
          <h3 className="font-medium text-blue-800" data-id="element-603">About Exceptions</h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-604">
            Exceptions are identified issues that require resolution before a
            loan can be included in a securitization or sale. Track, manage, and
            resolve exceptions to ensure compliance with investor and rating
            agency requirements.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-605">
        <div className="lg:col-span-1" data-id="element-606">
          <Card data-id="element-607">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-608">Filters</h3>
            <div className="space-y-6" data-id="element-609">
              <div data-id="element-610">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-611">
                  Exception Status
                </h4>
                <div className="space-y-2" data-id="element-612">
                  <label className="flex items-center" data-id="element-613">
                    <input type="radio" name="status" className="h-4 w-4 text-blue-600" checked={activeFilter === 'all'} onChange={() => setActiveFilter('all')} data-id="element-614" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-615">
                      All ({getStatusCount('all')})
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-616">
                    <input type="radio" name="status" className="h-4 w-4 text-blue-600" checked={activeFilter === 'open'} onChange={() => setActiveFilter('open')} data-id="element-617" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-618">
                      Open ({getStatusCount('open')})
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-619">
                    <input type="radio" name="status" className="h-4 w-4 text-blue-600" checked={activeFilter === 'in_review'} onChange={() => setActiveFilter('in_review')} data-id="element-620" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-621">
                      In Review ({getStatusCount('in_review')})
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-622">
                    <input type="radio" name="status" className="h-4 w-4 text-blue-600" checked={activeFilter === 'resolved'} onChange={() => setActiveFilter('resolved')} data-id="element-623" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-624">
                      Resolved ({getStatusCount('resolved')})
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-625">
                    <input type="radio" name="status" className="h-4 w-4 text-blue-600" checked={activeFilter === 'waived'} onChange={() => setActiveFilter('waived')} data-id="element-626" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-627">
                      Waived ({getStatusCount('waived')})
                    </span>
                  </label>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-628">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-629">
                  Severity
                </h4>
                <div className="space-y-2" data-id="element-630">
                  <label className="flex items-center" data-id="element-631">
                    <input type="radio" name="severity" className="h-4 w-4 text-blue-600" checked={selectedSeverity === 'all'} onChange={() => setSelectedSeverity('all')} data-id="element-632" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-633">
                      All Severities
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-634">
                    <input type="radio" name="severity" className="h-4 w-4 text-blue-600" checked={selectedSeverity === 'high'} onChange={() => setSelectedSeverity('high')} data-id="element-635" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-636">High</span>
                  </label>
                  <label className="flex items-center" data-id="element-637">
                    <input type="radio" name="severity" className="h-4 w-4 text-blue-600" checked={selectedSeverity === 'medium'} onChange={() => setSelectedSeverity('medium')} data-id="element-638" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-639">Medium</span>
                  </label>
                  <label className="flex items-center" data-id="element-640">
                    <input type="radio" name="severity" className="h-4 w-4 text-blue-600" checked={selectedSeverity === 'low'} onChange={() => setSelectedSeverity('low')} data-id="element-641" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-642">Low</span>
                  </label>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-643">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-644">
                  Category
                </h4>
                <div className="space-y-2" data-id="element-645">
                  <label className="flex items-center" data-id="element-646">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={selectedCategory === 'all'} onChange={() => setSelectedCategory('all')} data-id="element-647" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-648">
                      All Categories
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-649">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={selectedCategory === 'documentation'} onChange={() => setSelectedCategory('documentation')} data-id="element-650" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-651">
                      Documentation
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-652">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={selectedCategory === 'underwriting'} onChange={() => setSelectedCategory('underwriting')} data-id="element-653" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-654">
                      Underwriting
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-655">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={selectedCategory === 'compliance'} onChange={() => setSelectedCategory('compliance')} data-id="element-656" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-657">
                      Compliance
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-658">
                    <input type="radio" name="category" className="h-4 w-4 text-blue-600" checked={selectedCategory === 'valuation'} onChange={() => setSelectedCategory('valuation')} data-id="element-659" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-660">
                      Valuation
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-3" data-id="element-661">
          <Card data-id="element-662">
            <div className="flex justify-between items-center mb-6" data-id="element-663">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-664">
                Exception List
              </h3>
              <div className="flex items-center" data-id="element-665">
                <div className="relative" data-id="element-666">
                  <input type="text" placeholder="Search exceptions..." className="border border-gray-300 rounded-md pl-9 pr-3 py-2" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-667" />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-668">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-669">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" data-id="element-670" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-671">
              {filteredExceptions.length === 0 ? <div className="text-center py-8" data-id="element-672">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-673">
                    <CheckCircleIcon className="h-6 w-6 text-gray-400" data-id="element-674" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-675">
                    No exceptions found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-676">
                    No exceptions match your current filter criteria.
                  </p>
                </div> : filteredExceptions.map(exception => <div key={exception.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50" data-id="element-677">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start" data-id="element-678">
                      <div className="flex items-start" data-id="element-679">
                        <div className="mr-4" data-id="element-680">
                          {exception.status === 'open' && <AlertTriangleIcon className="w-5 h-5 text-red-500" data-id="element-681" />}
                          {exception.status === 'in_review' && <ClockIcon className="w-5 h-5 text-yellow-500" data-id="element-682" />}
                          {exception.status === 'resolved' && <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-683" />}
                          {exception.status === 'waived' && <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-684" />}
                        </div>
                        <div data-id="element-685">
                          <div className="flex items-center" data-id="element-686">
                            <h4 className="font-medium text-gray-900" data-id="element-687">
                              {exception.title}
                            </h4>
                            <span className="ml-2 text-xs text-gray-500" data-id="element-688">
                              {exception.id}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600" data-id="element-689">
                            {exception.description}
                          </p>
                          <div className="flex flex-wrap items-center mt-2 gap-2" data-id="element-690">
                            <span className="text-xs text-gray-500 flex items-center" data-id="element-691">
                              Loan: {exception.loanId} ({exception.borrower})
                            </span>
                            <span className="text-xs text-gray-500 flex items-center ml-3" data-id="element-692">
                              <ClockIcon className="w-3 h-3 mr-1" data-id="element-693" />
                              Due: {exception.dueDate}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center ml-3" data-id="element-694">
                              Assignee: {exception.assignee}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center mt-3 md:mt-0 gap-2" data-id="element-695">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(exception.status)}`} data-id="element-696">
                          {formatStatus(exception.status)}
                        </span>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityBadgeClass(exception.severity)}`} data-id="element-697">
                          {exception.severity.charAt(0).toUpperCase() + exception.severity.slice(1)}
                        </span>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryBadgeClass(exception.category)}`} data-id="element-698">
                          {exception.category.charAt(0).toUpperCase() + exception.category.slice(1)}
                        </span>
                      </div>
                    </div>
                    {exception.notes && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-699">
                        <p className="text-sm text-gray-600" data-id="element-700">
                          <span className="font-medium" data-id="element-701">Notes:</span>{' '}
                          {exception.notes}
                        </p>
                      </div>}
                    <div className="mt-3 flex justify-end" data-id="element-702">
                      <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-703">
                        View Details <ArrowRightIcon className="ml-1 w-3 h-3" data-id="element-704" />
                      </button>
                    </div>
                  </div>)}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}